/* eslint-disable react/display-name */
import { yupResolver } from '@hookform/resolvers/yup'
import CloseRounded from '@mui/icons-material/CloseRounded'
import {
  Box,
  Button,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { clientsAPI } from 'api/clientsAPI'
import { TaxiVehicleType } from 'api/models/Common/JyrneyEnums'
import { Location } from 'api/models/Common/Location'
import { FixedRouteSchema } from 'app/validationSchemas/ClientSchema/FixedRouteSchema'
import AddressSearch from 'components/Common/AddressSearch/AddressSearch'
import DirectionsMap from 'components/Common/DirectionsMap/DirectionsMap'
import ErrorMessage from 'components/Common/ErrorMessage/ErrorMessage'
import FormInput from 'components/FormFields/FormInput/FormInput'
import { Select } from 'components/RouteWizard/Select'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import ConfirmDialog from '../../../ConfirmDialog/ConfirmDialog'
import Notification from '../../../Notification/Notification'
import styles from './fixedRoutesStyle'
import FixedRoutesTable from './FixedRoutesTable'

export interface NotificationType {
  isOpen: boolean
  message: string
  type: string
}
export interface FixedRoutesProps {
  client: any
}

const FixedRoutes = ({ client }: FixedRoutesProps) => {
  const [openForm, setOpenForm] = React.useState(false)
  const [notify, setNotify] = React.useState<NotificationType>({
    isOpen: false,
    message: '',
    type: '',
  })
  const [confirmDialog, setConfirmDialog] = React.useState({
    isOpen: false,
    title: '',
    subtitle: '',
    onConfirm: '' as any,
  })
  const [mapPickup, setMapPickup] = React.useState<Location | undefined>()
  const [mapDropoff, setMapDropoff] = React.useState<Location | undefined>()
  // const [selectedRoute, setSelectedRoute] = React.useState<any>(null)
  const [partialPickup, setPartialPickup] = React.useState(false)
  const [partialDropoff, setPartialDropoff] = React.useState(false)
  const [selectedRoute, setSelectedRoute] = React.useState<any>(null)
  const [addressSearchVersion, setAddressSearchVersion] = React.useState(0)

  const updateRouteInfo = (routeInfo: any) => {
    console.log(routeInfo)
  }

  const clientFixedRoutes = useQuery(
    ['getClientFixedRoutes', client.id],
    async () => clientsAPI.getClientFixedRoutes(client.id),
  )

  React.useEffect(() => {
    console.log('DEV-220: Client Fixed Routes:', clientFixedRoutes.data)
  }, [clientFixedRoutes.data])

  const { mutateAsync: createRoute } = useMutation((payload: any) =>
    clientsAPI.createClientFixedRoute(payload.clientId, payload.fixedRoute),
  )
  const { mutateAsync: updateRoute } = useMutation((payload: any) =>
    clientsAPI.updateClientFixedRoute(
      payload.clientId,
      payload.fixedRouteId,
      payload.fixedRoute,
    ),
  )

  //New Route Form Info
  // const formMethods = useForm()
  const formMethods = useForm({
    resolver: yupResolver(FixedRouteSchema),
  })

  const resetDrawerState = () => {
    formMethods.reset({
      name: '',
      pickup: undefined,
      dropOff: undefined,
      rates: [{ vehicleTypeName: '', price: '' }],
    })
    setMapPickup(undefined)
    setMapDropoff(undefined)
    setPartialPickup(false)
    setPartialDropoff(false)
    setSelectedRoute(null)
    setAddressSearchVersion((current) => current + 1)
  }

  const closeForm = () => {
    setOpenForm(false)
    resetDrawerState()
  }

  const onSubmit = async (data: FormData, event: any) => {
    event.preventDefault()
    const payload = {
      fixedRoute: data,
      clientId: client.id,
    }
    console.log('DEV-220: Payload:', payload)
    if (selectedRoute?.id) {
      await updateRoute({
        ...payload,
        fixedRouteId: selectedRoute.id,
      })
    } else {
      await createRoute(payload)
    }
    await clientFixedRoutes.refetch()
    closeForm()
    setNotify({
      isOpen: true,
      message: selectedRoute?.id ? 'Route Updated' : 'Route Created',
      type: 'success',
    })
  }

  const handleSelectFrom = (value: any) => {
    formMethods.setValue('pickup', value)
    setMapPickup(value)
  }

  const handleSelectTo = (value: any) => {
    formMethods.setValue('dropOff', value)
    setMapDropoff(value)
  }

  const handleTogglePartialPickup = () => {
    setPartialPickup((prev) => !prev)
    formMethods.setValue('pickup', undefined)
    setMapPickup(undefined)
    setAddressSearchVersion((current) => current + 1)
  }

  const handleTogglePartialDropoff = () => {
    setPartialDropoff((prev) => !prev)
    formMethods.setValue('dropOff', undefined)
    setMapDropoff(undefined)
    setAddressSearchVersion((current) => current + 1)
  }

  const handlePartialPickupChange = (value: string) => {
    const upper = value.toUpperCase()
    formMethods.setValue('pickup', {
      postCode: upper,
      addressLine1: upper,
      formattedAddress: upper,
      isPartial: true,
    } as any)
  }

  const handlePartialDropoffChange = (value: string) => {
    const upper = value.toUpperCase()
    formMethods.setValue('dropOff', {
      postCode: upper,
      addressLine1: upper,
      formattedAddress: upper,
      isPartial: true,
    } as any)
  }

  const handleNewFixedRoute = () => {
    console.log('Button Pressed')
    resetDrawerState()
    setOpenForm(true)
  }

  const openExistingRoute = React.useCallback(
    (route: any) => {
      const defaultRate = route?.rates?.[0]

      setSelectedRoute(route)
      setMapPickup(route?.pickup?.isPartial ? undefined : route?.pickup)
      setMapDropoff(route?.dropOff?.isPartial ? undefined : route?.dropOff)
      setPartialPickup(Boolean(route?.pickup?.isPartial))
      setPartialDropoff(Boolean(route?.dropOff?.isPartial))
      formMethods.reset({
        name: route?.name || '',
        pickup: route?.pickup,
        dropOff: route?.dropOff,
        rates: [
          {
            vehicleTypeName: defaultRate?.vehicleTypeName || '',
            price: defaultRate?.price ?? '',
          },
        ],
      })
      setAddressSearchVersion((current) => current + 1)
      setOpenForm(true)
    },
    [formMethods],
  )

  // const previewName = formMethods.watch('name')
  const previewPickup = formMethods.watch('pickup')
  const previewDropoff = formMethods.watch('dropOff')
  // const previewVehicleType = formMethods.watch('rates[0].vehicleTypeName')
  // const previewPrice = formMethods.watch('rates[0].price')
  // const previewRates =
  //   previewVehicleType || previewPrice
  //     ? [
  //         {
  //           vehicleTypeName: previewVehicleType,
  //           price: previewPrice,
  //         },
  //       ]
  //     : []
  const isEditMode = Boolean(selectedRoute?.id)
  // const routePreview = {
  //   ...selectedRoute,
  //   name: previewName || selectedRoute?.name,
  //   pickup: previewPickup || selectedRoute?.pickup,
  //   dropOff: previewDropoff || selectedRoute?.dropOff,
  //   rates: previewRates.length > 0 ? previewRates : selectedRoute?.rates || [],
  // }
  const pickupAddressValue = previewPickup
    ? {
        value: previewPickup,
        label:
          previewPickup.formattedAddress ||
          [
            previewPickup.addressLine1,
            previewPickup.town,
            previewPickup.postCode,
          ]
            .filter(Boolean)
            .join(', '),
      }
    : null
  const dropoffAddressValue = previewDropoff
    ? {
        value: previewDropoff,
        label:
          previewDropoff.formattedAddress ||
          [
            previewDropoff.addressLine1,
            previewDropoff.town,
            previewDropoff.postCode,
          ]
            .filter(Boolean)
            .join(', '),
      }
    : null

  return (
    <Box component={'div'}>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={styles.buttonGrid}>
          <Button
            variant="outlined"
            className={`${styles.round} ${styles['tumblr']}`}
            onClick={() => {
              handleNewFixedRoute()
            }}
          >
            Add Fixed Route +
          </Button>
        </Grid>
        <Grid item xs={12} sx={styles.componentPanel}>
          <FixedRoutesTable
            clientFixedRoutes={clientFixedRoutes}
            setNotify={setNotify}
            client={client}
            setConfirmDialog={setConfirmDialog}
            setFixedRoute={(e) => {
              openExistingRoute(e)
            }}
          />
        </Grid>
        <Drawer
          anchor={'right'}
          open={openForm}
          onClose={closeForm}
          PaperProps={{ sx: styles.drawerPaper }}
        >
          <FormProvider {...formMethods}>
            <Box
              component="form"
              onSubmit={formMethods.handleSubmit(onSubmit)}
              sx={styles.drawerForm}
            >
              <Box sx={styles.drawerHeader}>
                <Box>
                  <Typography sx={styles.drawerEyebrow}>
                    Fixed Routes
                  </Typography>
                  <Typography variant="h5" sx={styles.drawerTitle}>
                    {isEditMode
                      ? 'Edit Client Fixed Route'
                      : 'Create Client Fixed Route'}
                  </Typography>
                </Box>
                <IconButton
                  aria-label="close fixed route panel"
                  onClick={closeForm}
                  sx={styles.closeButton}
                >
                  <CloseRounded />
                </IconButton>
              </Box>
              <Box sx={styles.drawerBody}>
                <Box sx={styles.formSection}>
                  <Box sx={styles.sectionHeader}>
                    <Box>
                      <Typography sx={styles.sectionTitle}>
                        Route setup
                      </Typography>
                      <Typography sx={styles.sectionDescription}>
                        Start with a clear name, then add the pickup and dropoff
                        you want to reuse.
                      </Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <FormInput
                        name={'name'}
                        label="Route name"
                        placeholder="Airport transfer, office shuttle, hospital run..."
                        sx={styles.routeNameInput}
                      />
                    </Grid>
                    {mapPickup && mapDropoff && (
                      <Grid item xs={12}>
                        <Box sx={styles.mapCard}>
                          <Typography sx={styles.mapTitle}>
                            Route preview
                          </Typography>
                          <DirectionsMap
                            origin={mapPickup}
                            destination={mapDropoff}
                            mapWidth="100%"
                            mapHeight="260px"
                            updateRouteInfo={updateRouteInfo}
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
                <Box sx={styles.formSection}>
                  <Box sx={styles.sectionHeader}>
                    <Box>
                      <Typography sx={styles.sectionTitle}>Stops</Typography>
                      <Typography sx={styles.sectionDescription}>
                        Choose whether each stop needs an exact match or can be
                        treated as a broader area.
                      </Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Box sx={styles.locationCard}>
                        <Box sx={styles.locationCardRow}>
                          <Box sx={styles.locationCardCopy}>
                            <Typography sx={styles.locationTitle}>
                              Pickup
                            </Typography>
                          </Box>
                          <Box sx={styles.locationSearchWrap}>
                            {partialPickup ? (
                              <TextField
                                fullWidth
                                placeholder="Partial postcode (e.g. CH3 5)"
                                defaultValue={
                                  previewPickup?.isPartial
                                    ? previewPickup.postCode
                                    : ''
                                }
                                onChange={(e) =>
                                  handlePartialPickupChange(e.target.value)
                                }
                              />
                            ) : (
                              <AddressSearch
                                key={`pickup-${addressSearchVersion}`}
                                onAddresssSelected={handleSelectFrom}
                                addressPlaceholder={'Pickup'}
                                name={'pickup'}
                                styles={styles.addressSearchStyles}
                                value={pickupAddressValue}
                                clientId={client.id}
                                requirePostcode
                              />
                            )}
                            <FormControlLabel
                              sx={styles.switchControl}
                              control={
                                <Switch
                                  checked={partialPickup}
                                  onChange={handleTogglePartialPickup}
                                  color="primary"
                                />
                              }
                              label={
                                <Typography sx={styles.switchLabel}>
                                  {partialPickup
                                    ? 'Partial pickup'
                                    : 'Exact pickup'}
                                </Typography>
                              }
                              labelPlacement="start"
                            />
                            <ErrorMessage
                              setFromResult={false}
                              message={
                                formMethods.formState.errors?.pickup
                                  ?.addressLine1.message
                              }
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={styles.locationCard}>
                        <Box sx={styles.locationCardRow}>
                          <Box sx={styles.locationCardCopy}>
                            <Typography sx={styles.locationTitle}>
                              Dropoff
                            </Typography>
                          </Box>
                          <Box sx={styles.locationSearchWrap}>
                            {partialDropoff ? (
                              <TextField
                                fullWidth
                                placeholder="Partial postcode (e.g. CH3 5)"
                                defaultValue={
                                  previewDropoff?.isPartial
                                    ? previewDropoff.postCode
                                    : ''
                                }
                                onChange={(e) =>
                                  handlePartialDropoffChange(e.target.value)
                                }
                              />
                            ) : (
                              <AddressSearch
                                key={`dropoff-${addressSearchVersion}`}
                                onAddresssSelected={handleSelectTo}
                                addressPlaceholder={'Drop Off'}
                                name={'dropOff'}
                                styles={styles.addressSearchStyles}
                                value={dropoffAddressValue}
                                clientId={client.id}
                                requirePostcode
                              />
                            )}
                            <FormControlLabel
                              sx={styles.switchControl}
                              control={
                                <Switch
                                  checked={partialDropoff}
                                  onChange={handleTogglePartialDropoff}
                                  color="primary"
                                />
                              }
                              label={
                                <Typography sx={styles.switchLabel}>
                                  {partialDropoff
                                    ? 'Partial dropoff'
                                    : 'Exact dropoff'}
                                </Typography>
                              }
                              labelPlacement="start"
                            />
                            <ErrorMessage
                              setFromResult={false}
                              message={
                                formMethods.formState.errors?.dropOff
                                  ?.addressLine1.message
                              }
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={styles.formSection}>
                  <Box sx={styles.sectionHeader}>
                    <Box>
                      <Typography sx={styles.sectionTitle}>Pricing</Typography>
                      <Typography sx={styles.sectionDescription}>
                        Add the first vehicle rate now. You can extend pricing
                        tiers later if needed.
                      </Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={2} sx={styles.pricingRow}>
                    <Grid item xs={12} sm={8}>
                      <Select
                        control={formMethods.control}
                        name={'rates[0].vehicleTypeName'}
                        label="Vehicle Type"
                        options={TaxiVehicleType}
                        searchable
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormInput
                        name={'rates[0].price'}
                        label="Price"
                        type="number"
                        sx={styles.priceInput}
                        inputProps={{ min: 0, step: '0.01' }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              GBP
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              <Box sx={styles.drawerFooter}>
                {/* <Button
                  type="button"
                  variant="text"
                  onClick={closeForm}
                  sx={styles.closeAction}
                >
                  Close
                </Button> */}
                <Box sx={styles.footerActionGroup}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={styles.primaryAction}
                  >
                    {isEditMode ? 'Save changes' : 'Save route'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </FormProvider>
        </Drawer>
        <Notification notify={notify} setNotify={setNotify} />
        <ConfirmDialog
          confirmDialog={confirmDialog}
          setConfirmDialog={setConfirmDialog}
        />
      </Grid>
    </Box>
  )
}

export default FixedRoutes
